<script>
    import R, {LOADER} from "../lib/requests";
    import user from "../stores/user";
    import Notifier from "../stores/notifier";
    import {USER_ROLE, USER_ROLES} from "../lib/constants";
    import {setFocusTo, trim, isCorrectMobile} from "../lib/lib";
    import Modal from '../components/modal/Modal.svelte';
    import Checkbox from "../components/controls/Checkbox.svelte";
    import DriverControl from "../components/controls/DriverControl.svelte";
    import LocationControl from "../components/controls/LocationControl.svelte";
    import TransporterSelectControl from "../components/controls/TransporterSelectControl.svelte";
    import TestNotificationModal from "./TestNotificationModal.svelte";

    export let userId = null;
    export let departmentId = null;
    export let transporterId = null;
    export let driver = null;
    export let closeCallback;

    let userData = null;
    let changePassword = false;
    let userRoles = [];
    let allowNotifications = true;
    let showTestPopup = false;

    const notificationTypes = [
        'Without notification',
        'SMS message',
        'WhatsApp message'
    ];

    const initialize = () => {
        Object.keys(USER_ROLES).map(key => {
            userRoles.push({id: Number(key), name: USER_ROLES[key]});
        });
        if ($user.role == USER_ROLE.ADMIN) {
            // Admins can not create gods
            userRoles = userRoles.filter(x => x.id != USER_ROLE.GOD);
        }

        if (userId == 0) {
            userData = {
                id: 0,
                driver_id: 0,
                transporter_id: '0',
                role: USER_ROLE.ADMIN,
                name: '',
                mobile: '',
                email: '',
                username: '',
                password: '',
                notes: '',
                notification_type: 0,
                notification_enabled: 0,
                is_active: 1,
                driver: null,
                location: null,
                department_id: departmentId,
            }
            if (driver) {
                userData.role = `${USER_ROLE.DRIVER}`;
                userData.name = driver.name;
                userData.email = driver.email;
                userData.mobile = driver.mobile;
                userData.driver_id = driver.id;
                userData.driver = driver;
            } else if (transporterId != null) {
                userData.role = USER_ROLE.TRANSPORTER;
                userData.transporter_id = transporterId;
            }
            setFocusTo('userName');
        } else {
            load();
        }
    }

    const load = () => {

        const onSuccess = result => {
            userData = {
                ...result,
                notification_type: Number(result.notification_type),
                role: Number(result.role)
            };
            setFocusTo('userName');
        };

        const onFailure = err => {
        };

        R.handle(R.user.details, userId, onSuccess, onFailure, LOADER.POPUP);
    };


    const save = () => {
        const onSuccess = result => {
            Notifier.showMessage("User account updated");
            close(result);
        }

        const onFailure = err => {
            console.log(err);
            if (err.message) {
                Notifier.showError(err.message);
                if (err.code == 'user_account_exist') {
                    setFocusTo("userEmail");
                }
            }
        };


        userData.name = userData.name.trim();
        userData.username = userData.username.trim();
        if (!userData.name) {
            Notifier.showError("Type the name of the user");
            setFocusTo("userName");
            return;
        }
        if (!userData.username) {
            Notifier.showError("Type the name for user account");
            setFocusTo("userUsername");
            return;
        }
        if (userData.id == 0 && !userData.password) {
            Notifier.showError("Type the password for user account");
            setFocusTo("password");
            return;
        }
        if (changePassword && !userData.password) {
            Notifier.showError("Type the new password for user account");
            setFocusTo("newPassword");
            return;
        }

        if (userData.role == USER_ROLE.DRIVER && !userData.driver_id) {
            Notifier.showError("Driver not selected!");
            setFocusTo("driverName");
            return;
        }

        if (userData.role == USER_ROLE.AIRPORT && !userData.location_id) {
            Notifier.showError("Airport location not selected!");
            setFocusTo("locationName");
            return;
        }

        if (userData.role == USER_ROLE.TRANSPORTER && userData.transporter_id == 0) {
            Notifier.showError("Transport company not selected!");
            setFocusTo("transporter");
            return;
        }

        if (!allowNotifications) {
            userData.notification_type = 0;
            userData.notification_enabled = 0;
        }

        userData.mobile = trim(userData.mobile);
        if (userData.mobile) {
            // remove spaces, +, -, etc
            userData.mobile = userData.mobile.replace(/\D/g, '');

            // add Greece country code if missing
            if (!userData.mobile.startsWith('30')) {
                userData.mobile = `30${userData.mobile}`;
            }
        }

        if (!isCorrectMobile(userData.mobile)) {
            Notifier.showError("Mobile number is not correct!");
            setFocusTo("userMobile");
            return;
        }

        if (userData.notification_type > 0 && !userData.mobile) {
            Notifier.showError("No mobile number set for notifications!");
            setFocusTo("userMobile");
            return;
        }

        if (userData.id > 0 && !changePassword) {
            delete (userData.password);
        }

        let action = userId == 0 ? 'create' : 'edit';
        R.handle(R.user[action], userData, onSuccess, onFailure, LOADER.POPUP);
    }

    const close = (param) => {
        userData = null;

        if (param instanceof Event) {
            closeCallback();
            return;
        }

        setTimeout(() => {
            closeCallback(param);
        }, 200);
    }

    const toggleChangePassword = () => {
        changePassword = !changePassword;
        if (changePassword) {
            setFocusTo("newPassword");
        } else {
            delete (userData.password);
        }
    }

    const onDriverChange = ({detail}) => {
        userData.driver = detail;
        if (detail) {
            userData.driver_id = detail.id;
            if (!userData.name) {
                userData.name = detail.name;
            }
            if (!userData.email) {
                userData.email = detail.email;
            }
            if (!userData.mobile) {
                userData.mobile = detail.mobile;
            }
        } else {
            userData.driver_id = 0;
        }
    }

    const onLocationChange = ({detail}) => {
        userData.location = detail;
        userData.location_id = detail ? detail.id : 0;
    }

    initialize();

    $: if (userData) {
        allowNotifications = userData.role == USER_ROLE.ADMIN || userData.role == USER_ROLE.LOCAL_ADMIN || userData.role == USER_ROLE.DRIVER;
    }
</script>

{#if userData}
    <Modal title="User account" on:close="{ close }">
        <div slot="body">
            <div class="form-group">
                <label>Name</label>
                <input type="text" class="form-control" id="userName" bind:value="{ userData.name }">
            </div>
            <div class="form-group">
                <label>Mobile</label>
                <input type="tel" class="form-control" id="userMobile" bind:value="{ userData.mobile }">
                <div class="small">Mobile number must contain country code (30)</div>
            </div>
            <div class="form-group">
                <label>Email</label>
                <input type="email" class="form-control" id="userEmail" bind:value="{ userData.email }">
            </div>
            <div class="form-group">
                <label>User name</label>
                <input type="email" class="form-control" id="userUserName" bind:value="{ userData.username }">
            </div>
            {#if userData.id == 0}
                <div class="form-group">
                    <label>Password</label>
                    <input type="text" class="form-control" id="password" bind:value="{ userData.password }">
                </div>
            {:else}
                {#if !changePassword}
                    <div class="form-group">
                        <button class="btn btn-link ps-0" on:click="{ toggleChangePassword }">
                            Set new password
                        </button>
                    </div>
                {:else}
                    <div class="form-group">
                        <label>New password</label>
                        <input type="text" class="form-control" id="newPassword" bind:value="{ userData.password }">
                        <button class="btn btn-link ps-0" on:click="{ toggleChangePassword }">
                            Do not change password
                        </button>
                    </div>
                {/if}
            {/if}
            <div class="row align-items-center">
                {#if transporterId == null}
                    <div class="col">
                        <div class="form-group">
                            <select class="form-select" bind:value={userData.role} disabled={userData.id == 1}>
                                {#each userRoles as item}
                                    <option value={item.id}>{item.name}</option>
                                {/each}
                            </select>
                        </div>
                    </div>
                {/if}
                <div class="col">
                    <div class="form-group">
                        <Checkbox label="Active account" bind:checked={userData.is_active} disabled={userData.id == 1}/>
                    </div>
                </div>
            </div>
            {#if allowNotifications}
                <div class="form-group">
                    <label>Notification type</label>
                    <select class="form-select mb-2" bind:value={userData.notification_type}>
                        {#each notificationTypes as type, index}
                            <option value={index}>{type}</option>
                        {/each}
                    </select>
                    <div class="d-flex align-items-center justify-content-between">
                        <Checkbox label="Enable notifications" bind:checked={userData.notification_enabled}/>
                        <button class="btn btn-link" on:click={() => showTestPopup = true}>test notification</button>
                    </div>
                </div>
            {/if}
            {#if userData.role == USER_ROLE.DRIVER}
                <div class="form-group">
                    <label>Driver</label>
                    <DriverControl driver="{ userData.driver }" placeholder="set driver"
                                   on:change="{ onDriverChange }"/>
                </div>
            {:else if userData.role == USER_ROLE.AIRPORT}
                <div class="form-group">
                    <label>Airport location</label>
                    <LocationControl location="{ userData.location }" placeholder="set location"
                                     on:change="{ onLocationChange }"/>
                </div>
            {:else if userData.role == USER_ROLE.TRANSPORTER}
                <div class="form-group">
                    <label>Transport company</label>
                    <TransporterSelectControl id="transporter" bind:transporterId={userData.transporter_id}
                                              placeholder="select user transport company"/>
                </div>
            {:else if userData.role == USER_ROLE.DEPARTMENT}
                <div class="form-group">
                    <label>Departments</label>
                    {#if userData.departments && userData.departments.length > 0}
                        <ul class="list-group">
                            {#each userData.departments as department}
                                <li class="list-group-item">{department.name}</li>
                            {/each}
                        </ul>
                    {/if}
                </div>
            {/if}
            <div class="form-group">
                <label>Notes</label>
                <textarea class="form-control" bind:value="{ userData.notes }"></textarea>
            </div>
            <hr>
            <div class="text-end">
                <div class="btn btn-link me-2" on:click="{ () => close() }">Close</div>
                <button class="btn btn-primary px-4" on:click="{ save }">Save</button>
            </div>
        </div>
    </Modal>
{/if}

{#if showTestPopup}
    <TestNotificationModal notification_type={userData.notification_type} mobile={userData.mobile}
                           onClose={() => showTestPopup = false}/>
{/if}